import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { useNavigate } from 'react-router-dom';
import ProductAutocomplete from '../../components/common/ProductAutocomplete';


const StockTransfer = () => {
    const { user } = useAuth();
    const { showToast } = useUI();
    const navigate = useNavigate();
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        sku: '',
        fromWarehouseId: '',
        toWarehouseId: '',

        quantity: 1,
        unit: '',
        description: ''
    });



    const fetchData = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            const wRes = await fetch(`/api/warehouses?companyId=${user.companyId}`);
            if (wRes.ok) {
                const wData = await wRes.json();
                setWarehouses(wData);
            }

        } catch (error) {
            console.error(error);
        }
    }, [user?.companyId, formData.fromWarehouseId, formData.toWarehouseId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTransfer = async (e) => {
        e.preventDefault();

        if (!formData.sku || !formData.fromWarehouseId || !formData.toWarehouseId || formData.quantity <= 0) {
            showToast("Veuillez remplir tous les champs", "warning");
            return;
        }

        if (formData.fromWarehouseId === formData.toWarehouseId) {
            showToast("L'entrepôt de départ et d'arrivée doivent être différents", "warning");
            return;
        }

        setLoading(true);
        try {
            // Find product by SKU in source warehouse
            const prodRes = await fetch(`/api/products/search?sku=${formData.sku}&warehouseId=${formData.fromWarehouseId}&companyId=${user.companyId}`);
            const prodData = await prodRes.json();

            if (!prodRes.ok || !prodData || prodData.length === 0) {
                showToast("Produit non trouvé dans l'entrepôt de départ", "error");
                setLoading(false);
                return;
            }

            const product = prodData[0];

            if (product.quantity < formData.quantity) {
                showToast(`Stock insuffisant (${product.quantity} disponibles)`, "error");
                setLoading(false);
                return;
            }

            const response = await fetch('/api/stock-movements/transfer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId: product.id,
                    fromWarehouseId: formData.fromWarehouseId,
                    toWarehouseId: formData.toWarehouseId,
                    quantity: formData.quantity,
                    unit: formData.unit,
                    userId: user.id,
                    companyId: user.companyId,
                    description: formData.description
                })

            });

            if (response.ok) {
                showToast("Transfert validé avec succès", "success");
                setFormData(prev => ({ ...prev, sku: '', quantity: 1, unit: '', description: '' }));
            } else {


                const error = await response.json();
                showToast(error.error || "Erreur lors du transfert", "error");
            }
        } catch (error) {
            console.error(error);
            showToast("Erreur serveur", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-8">
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <span className="material-symbols-outlined">arrow_back</span>
                    </button>
                    <div>
                        <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Transfert Entre Entrepôts</h1>
                        <p className="text-slate-500 dark:text-slate-400 mt-1">Déplacez vos stocks de manière sécurisée et tracée</p>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto w-full">
                <form onSubmit={handleTransfer} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-8 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20">
                        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 relative">
                            {/* Source */}
                            <div className="flex-1 w-full space-y-2">
                                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest text-center">Départ</label>
                                <div className="p-4 bg-white dark:bg-slate-800 rounded-xl border-2 border-primary/20 flex flex-col items-center gap-2 group hover:border-primary transition-all">
                                    <span className="material-symbols-outlined text-3xl text-primary font-bold">warehouse</span>
                                    <select
                                        className={`w-full h-10 bg-transparent border-none text-center font-black text-slate-900 dark:text-white outline-none cursor-pointer ${!formData.fromWarehouseId ? 'text-amber-500' : ''}`}
                                        value={formData.fromWarehouseId}
                                        onChange={e => setFormData({ ...formData, fromWarehouseId: e.target.value })}
                                    >
                                        <option value="">-- Choisir --</option>
                                        {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                    </select>


                                </div>
                            </div>

                            {/* Arrow */}
                            <div className="size-12 rounded-full bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/30 z-10 shrink-0">
                                <span className="material-symbols-outlined font-black">arrow_forward</span>
                            </div>

                            {/* Destination */}
                            <div className="flex-1 w-full space-y-2">
                                <label className="block text-xs font-black text-slate-400 uppercase tracking-widest text-center">Destination</label>
                                <div className="p-4 bg-white dark:bg-slate-800 rounded-xl border-2 border-emerald-500/20 flex flex-col items-center gap-2 group hover:border-emerald-500 transition-all">
                                    <span className="material-symbols-outlined text-3xl text-emerald-500 font-bold">factory</span>
                                    <select
                                        className={`w-full h-10 bg-transparent border-none text-center font-black text-slate-900 dark:text-white outline-none cursor-pointer ${!formData.toWarehouseId ? 'text-amber-500' : ''}`}
                                        value={formData.toWarehouseId}
                                        onChange={e => setFormData({ ...formData, toWarehouseId: e.target.value })}
                                    >
                                        <option value="">-- Choisir --</option>
                                        {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                    </select>


                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-8 space-y-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">Référence Article (SKU)</label>
                                    <div className="relative group">
                                        <ProductAutocomplete
                                            value={formData.sku}
                                            onChange={val => setFormData({ ...formData, sku: val })}
                                            onSelect={product => setFormData({ ...formData, sku: product.sku })}
                                            companyId={user?.companyId}
                                            warehouseId={formData.fromWarehouseId}
                                            placeholder="Chercher le produit à transférer..."
                                        />
                                    </div>

                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">Unité & Quantité</label>
                                    <div className="flex items-center gap-2">
                                        <select
                                            className={`h-14 px-4 pr-10 bg-slate-100 dark:bg-slate-800 border-2 rounded-xl focus:ring-2 focus:ring-primary outline-none font-bold text-sm text-slate-900 dark:text-white cursor-pointer ${!formData.unit ? 'border-amber-500/50' : 'border-transparent'}`}
                                            value={formData.unit}
                                            onChange={e => setFormData({ ...formData, unit: e.target.value })}
                                        >
                                            <option value="">--Choisir--</option>
                                            <option value="Unité">Unité</option>
                                            <option value="Carton">Carton</option>
                                            <option value="Palette">Palette</option>
                                        </select>

                                        <input
                                            required
                                            className="flex-1 h-14 text-center bg-slate-50 dark:bg-slate-800 border-none rounded-xl focus:ring-2 focus:ring-primary outline-none font-black text-xl text-slate-900 dark:text-white"
                                            type="number"
                                            min="1"
                                            value={formData.quantity}
                                            onChange={e => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                                        />

                                    </div>
                                </div>

                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-400 uppercase tracking-widest mb-2">Note de transfert (optionnel)</label>
                                <textarea
                                    className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-primary text-slate-900 dark:text-white font-medium resize-none h-[148px]"
                                    placeholder="Raison du transfert, référence BL..."
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-4 bg-primary text-white font-black rounded-xl text-lg shadow-xl shadow-primary/20 hover:bg-primary/90 transition-all active:scale-[0.98] disabled:opacity-50"
                            >
                                {loading ? "Traitement en cours..." : "Valider le transfert"}
                            </button>
                            <p className="text-center text-xs text-slate-500 font-bold mt-4 uppercase tracking-widest">
                                <span className="material-symbols-outlined text-xs align-middle mr-1">security</span>
                                Chaque transfert est tracé et consultable dans l'historique
                            </p>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StockTransfer;
